import { useState } from 'react';
import { ArrowLeft, Loader2, Mail, Lock, CheckCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

interface SecuritySectionProps {
  onBack: () => void;
}

const passwordSchema = z.object({
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

export function SecuritySection({ onBack }: SecuritySectionProps) {
  const { user, loginProvider } = useAuth();
  const isOAuthUser = loginProvider === 'google' || loginProvider === 'apple';
  const { toast } = useToast();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleUpdatePassword = async () => {
    const validation = passwordSchema.safeParse({ newPassword, confirmPassword });
    if (!validation.success) {
      toast({
        title: 'Validation Error',
        description: validation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: 'Password updated',
        description: 'Your password has been changed successfully.',
      });
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update password. Please try again.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user?.email) return;

    setIsSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: 'Reset email sent',
        description: 'Check your inbox for the password reset link.',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email. Please try again.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Security</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-8">
        {/* OAuth users: show login method info instead of password */}
        {isOAuthUser && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <h3 className="font-medium">Sign in with {loginProvider === 'google' ? 'Google' : 'Apple'}</h3>
            </div>
            <div className="rounded-lg border bg-muted/30 p-4 pl-7">
              <p className="text-sm text-muted-foreground">
                Your account is managed by {loginProvider === 'google' ? 'Google' : 'Apple'}. Sign out and use the same provider to sign back in. There is no password to change.
              </p>
            </div>
          </div>
        )}

        {/* Change Password - only for email users */}
        {!isOAuthUser && (
          <>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-medium">Change Password</h3>
              </div>
              
              <div className="space-y-4 pl-7">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>

                <Button
                  onClick={handleUpdatePassword}
                  disabled={isUpdatingPassword || !newPassword || !confirmPassword}
                  className="w-full"
                >
                  {isUpdatingPassword ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </div>
            </div>

            <Separator />

            {/* Password Reset Email */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-medium">Password Reset</h3>
              </div>
              
              <div className="space-y-4 pl-7">
                <p className="text-sm text-muted-foreground">
                  Forgot your password or want to reset it via email? We'll send a secure reset link to <strong>{user?.email}</strong>.
                </p>

                {resetEmailSent ? (
                  <div className="flex items-center gap-2 text-success">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">Reset email sent!</span>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleSendPasswordReset}
                    disabled={isSendingReset}
                    className="w-full"
                  >
                    {isSendingReset ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Send Reset Email'
                    )}
                  </Button>
                )}
              </div>
            </div>

            <Separator />
          </>
        )}

        {/* Account Info */}
        <div className="space-y-2">
          <h3 className="font-medium">Account Info</h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Email: {user?.email}</p>
            <p>Sign in method: {loginProvider === 'google' ? 'Google' : loginProvider === 'apple' ? 'Apple' : 'Email & password'}</p>
            <p>Last sign in: {user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
